import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Monitor, 
  Package, 
  Cpu, 
  Code, 
  Wrench,
  ArrowRight,
  Calendar,
  Globe,
  Users,
  Headphones
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stats = [
  { value: '2014', label: 'stats.years', icon: Calendar },
  { value: '24/7', label: 'stats.support', icon: Headphones },
  { value: '12', label: 'stats.countries', icon: Globe },
  { value: '571+', label: 'stats.clients', icon: Users },
];

const departments = [
  { 
    key: 'rental', 
    icon: Monitor, 
    href: '/rental',
    image: 'https://images.unsplash.com/photo-1612125033189-3a4950acc118?w=600'
  },
  { 
    key: 'sales', 
    icon: Package, 
    href: '/sales',
    image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=600'
  },
  { 
    key: 'parts', 
    icon: Cpu, 
    href: '/parts',
    image: 'https://images.unsplash.com/photo-1603732551681-2e91159b9dc2?w=600'
  },
  { 
    key: 'software', 
    icon: Code, 
    href: '/software',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600'
  },
  { 
    key: 'services', 
    icon: Wrench, 
    href: '/services',
    image: 'https://images.unsplash.com/photo-1753964724380-2c5ae02512a8?w=600'
  },
];

const clientLogos = [
  'Ville de Chambly', 'Ville de Varennes', 'Ville de Sainte-Julie',
  'Bell Media', 'Hockey Supremacy', 'Royer'
];

export default function Home() {
  const { t } = useTranslation();
  const [realizations, setRealizations] = useState([]);

  useEffect(() => {
    fetchRealizations();
  }, []);

  const fetchRealizations = async () => {
    try {
      const response = await axios.get(`${API}/realizations?featured=true`);
      setRealizations(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching realizations:', error);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gray-50" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1694253987646-353954f1889d?w=1920"
            alt="Digital signage"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight"
            >
              {t('hero.title')}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 mb-8 max-w-xl"
            >
              {t('hero.subtitle')}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/rental">
                <Button size="lg" className="bg-black text-white hover:bg-gray-800 text-lg px-8" data-testid="hero-cta-btn">
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-black text-black hover:bg-gray-100 text-lg px-8" data-testid="hero-contact-btn">
                  {t('hero.contact')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-gray-200 bg-white" data-testid="stats-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg mb-4">
                    <Icon className="h-6 w-6 text-black" />
                  </div>
                  <p className="text-3xl font-bold text-black mb-1 font-mono">{stat.value}</p>
                  <p className="text-gray-500 text-sm">{t(stat.label)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Departments Grid */}
      <section className="py-24 bg-gray-50" data-testid="departments-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">Nos Services</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Solutions complètes d'affichage numérique pour tous vos besoins
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {departments.map((dept, index) => {
              const Icon = dept.icon;
              return (
                <motion.div
                  key={dept.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={dept.href} data-testid={`dept-card-${dept.key}`}>
                    <Card className="group bg-white border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 h-full">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={dept.image}
                          alt={t(`dept.${dept.key}`)}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-semibold text-black">{t(`dept.${dept.key}`)}</h3>
                            </div>
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <p className="text-gray-500 text-sm">{t(`dept.${dept.key}.desc`)}</p>
                        <div className="mt-4 flex items-center text-black text-sm font-medium group-hover:translate-x-2 transition-transform">
                          En savoir plus
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Realizations Preview */}
      {realizations.length > 0 && (
        <section className="py-24 bg-white border-y border-gray-200" data-testid="realizations-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="flex justify-between items-end mb-12"
            >
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">Nos Réalisations</h2>
                <p className="text-gray-500">Découvrez nos projets récents</p>
              </div>
              <Link to="/realizations">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                  Voir tout
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {realizations.map((realization, index) => (
                <motion.div
                  key={realization.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="group bg-white border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={realization.images?.[0] || 'https://images.unsplash.com/photo-1694253987646-353954f1889d'}
                        alt={realization.title_fr}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 font-mono">
                        {realization.client_name}
                      </p>
                      <h3 className="text-lg font-semibold text-black mb-2">{realization.title_fr}</h3>
                      <p className="text-gray-500 text-sm line-clamp-2">{realization.description_fr}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="py-24 bg-gray-50" data-testid="trust-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">Ils nous font confiance</h2>
            <p className="text-gray-500">Plus de 571 clients satisfaits à travers le monde</p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {clientLogos.map((logo, index) => (
              <motion.div
                key={logo}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="px-6 py-3 bg-white border border-gray-200 rounded-lg"
              >
                <span className="text-gray-600 font-medium">{logo}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Prêt à transformer votre communication visuelle?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Contactez-nous pour une démo personnalisée et découvrez comment nos solutions peuvent propulser votre entreprise.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-black hover:bg-gray-200 text-lg px-8">
                  Planifier une rencontre
                </Button>
              </Link>
              <a href="tel:+15141234567">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                  Appeler maintenant
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
